import type { Date as PDate } from "../../generated/core/v1/date";
import { type Event, Event_Type, type ShortfallDetails } from "../../generated/core/v1/events";
import type { FinancialPlanData } from "../../generated/core/v1/financial_plan";
import {
  ShortfallFix_Type,
  type ShortfallFix,
  type ShortfallFixes,
} from "../../generated/core/v1/shortfall";
import { monthsBetween } from "../lib/dates";
import { eventsOf, startDate } from "./core";

/** The plan's first CASH_SHORTFALL event (the date the money on hand first doesn't cover what's due). */
export function firstShortfall(plan: FinancialPlanData | undefined | null): Event | undefined {
  return eventsOf(plan, Event_Type.CASH_SHORTFALL)[0];
}

export function hasShortfall(plan: FinancialPlanData | undefined | null): boolean {
  return firstShortfall(plan) !== undefined;
}

export type ShortfallView = {
  date: PDate;
  /** Cash short on that day: what the failing payment lacked (not a monthly figure). */
  amount: number;
  /** The deepest point across the plan's shortfall events. */
  peak: number;
  monthsAway: number;
  details?: ShortfallDetails;
};

export function shortfallView(
  plan: FinancialPlanData | undefined | null,
): ShortfallView | undefined {
  const all = eventsOf(plan, Event_Type.CASH_SHORTFALL);
  const first = all[0];
  if (!first?.date) return undefined;
  const amt = (e: Event) =>
    e.details.oneofKind === "shortfall" ? e.details.shortfall.shortfallAmount : 0;
  const start = startDate(plan);
  return {
    date: first.date,
    amount: amt(first),
    peak: Math.max(...all.map(amt)),
    monthsAway: start ? Math.max(0, monthsBetween(start, first.date)) : 0,
    details: first.details.oneofKind === "shortfall" ? first.details.shortfall : undefined,
  };
}

/** Fixes in the order the page lists them: those that close it entirely first, then by amount. */
export function sortedFixes(fixes: ShortfallFixes | undefined): ShortfallFix[] {
  if (!fixes) return [];
  return [...fixes.fixes].sort((a, b) => {
    const ca = a.newShortfallDate ? 1 : 0;
    const cb = b.newShortfallDate ? 1 : 0;
    return ca - cb || b.closesAmount - a.closesAmount;
  });
}

/** The fix that closes it entirely with the smallest monthly amount, for the Home banner. */
export function mostEfficientFix(fixes: ShortfallFixes | undefined): ShortfallFix | undefined {
  const closing = (fixes?.fixes ?? []).filter((f) => !f.newShortfallDate);
  const monthly = closing.filter((f) => f.type !== ShortfallFix_Type.LUMP_SUM_TODAY);
  const pool = monthly.length ? monthly : closing;
  return [...pool].sort((a, b) => a.amount - b.amount)[0];
}
